<template>
  <div class="modal-overlay" @click.self="closeModal">
    <div class="modal-container glass">
      <div class="modal-header">
        <div class="header-content">
          <span class="briefcase-icon glass-sm">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10 6V5a2 2 0 0 1 2-2h0a2 2 0 0 1 2 2v1" />
              <rect x="3" y="6" width="18" height="15" rx="2" />
              <path d="M3 12h18" />
              <path d="M10 12v2h4v-2" />
            </svg>
          </span>
          <h2 class="modal-title">{{ modalTitle }}</h2>
        </div>
        <button class="close-btn" @click="closeModal" :aria-label="$t('common.close')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <form novalidate @submit.prevent="handleSubmit">
          <div class="form-row three-cols">
            <div class="form-group">
              <label>{{ $t('careerModal.name') }} <span class="required">*</span></label>
              <input
                type="text"
                v-model="form.name"
                :placeholder="$t('careerModal.namePlaceholder')"
                :maxlength="fieldLimits.name"
                required
                class="glass-sm"
                @input="clearFieldError('name')"
              />
              <div class="field-meta">
                <p v-if="fieldErrors.name" class="form-error">{{ fieldErrors.name }}</p>
                <span class="char-count">{{ form.name.length }}/{{ fieldLimits.name }}</span>
              </div>
            </div>
            <div class="form-group">
              <label>{{ $t('careerModal.phone') }} <span class="required">*</span></label>
              <input
                type="tel"
                v-model="form.phone"
                :placeholder="$t('careerModal.phonePlaceholder')"
                :minlength="fieldLimits.phoneMin"
                :maxlength="fieldLimits.phoneMax"
                required
                class="glass-sm"
                @input="handlePhoneInput"
              />
              <div class="field-meta">
                <p v-if="fieldErrors.phone" class="form-error">{{ fieldErrors.phone }}</p>
                <span class="char-count">{{ form.phone.length }}/{{ fieldLimits.phoneMax }}</span>
              </div>
            </div>
            <div class="form-group">
              <label>{{ $t('careerModal.email') }} <span class="required">*</span></label>
              <input
                type="email"
                v-model="form.email"
                :placeholder="$t('careerModal.emailPlaceholder')"
                :maxlength="fieldLimits.email"
                required
                class="glass-sm"
                @input="handleEmailInput"
              />
              <div class="field-meta">
                <p v-if="fieldErrors.email" class="form-error">{{ fieldErrors.email }}</p>
                <span class="char-count">{{ form.email.length }}/{{ fieldLimits.email }}</span>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>{{ $t('careerModal.experience') }} <span class="required">*</span></label>
            <textarea
              v-model="form.experience"
              rows="5"
              :placeholder="$t('careerModal.experiencePlaceholder')"
              :maxlength="fieldLimits.experience"
              required
              class="glass-sm"
              @input="clearFieldError('experience')"
            ></textarea>
            <div class="field-meta">
              <p v-if="fieldErrors.experience" class="form-error">{{ fieldErrors.experience }}</p>
              <span class="char-count">{{ form.experience.length }}/{{ fieldLimits.experience }}</span>
            </div>
          </div>

          <div class="form-group attachment-group">
            <label>{{ $t('careerModal.attachment') }}</label>
            <input
              ref="resumeInputRef"
              type="file"
              class="sr-only-file"
              :accept="resumeAcceptTypes"
              @change="handleResumeChange"
            />
            <div class="attachment-line">
              <button type="button" class="attachment-link" @click="selectResume">
                {{ form.resumeFileName ? $t('careerModal.attachmentReplace') : $t('careerModal.attachmentChoose') }}
              </button>
              <span class="attachment-name">
                {{ form.resumeFileName || $t('careerModal.attachmentHint') }}
              </span>
              <button
                v-if="form.resumeFileName"
                type="button"
                class="attachment-clear"
                @click="clearResume"
              >
                {{ $t('careerModal.attachmentClear') }}
              </button>
            </div>
            <p v-if="fileError" class="form-error">{{ fileError }}</p>
          </div>

          <div class="submit-section">
            <button type="submit" class="submit-btn" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="btn-spinner" aria-hidden="true"></span>
              <span>{{ isSubmitting ? $t('careerModal.submitting') : $t('careerModal.submit') }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, defineEmits, defineProps } from 'vue'
import { useI18n } from 'vue-i18n'
import { submitCareerContact } from '@/api/websiteContact'
import { isEmailCharactersValid, isPhoneCharactersValid, sanitizeEmail, sanitizePhone } from '@/utils/contactForm'
import { showToast } from '@/composables/useToast'

const props = defineProps({
  contactType: {
    type: Number,
    required: true
  },
  titleKey: {
    type: String,
    required: true
  }
})

const { t } = useI18n()
const emit = defineEmits(['close'])

const modalTitle = computed(() => t(props.titleKey))
const isSubmitting = ref(false)
const fileError = ref('')
const resumeInputRef = ref(null)
const resumeAcceptTypes = '.pdf,.doc,.docx,.jpg,.jpeg,.png'
const maxResumeSize = 10 * 1024 * 1024
const allowedResumeExtensions = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png']
const fieldLimits = {
  name: 20,
  phoneMin: 6,
  phoneMax: 20,
  email: 32,
  experience: 5000
}
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const form = reactive({
  name: '',
  phone: '',
  email: '',
  experience: '',
  resumeFile: null,
  resumeFileName: ''
})

const fieldErrors = reactive({
  name: '',
  phone: '',
  email: '',
  experience: ''
})

const closeModal = () => {
  emit('close')
}

const clearFieldError = (field) => {
  fieldErrors[field] = ''
}

const handlePhoneInput = () => {
  form.phone = sanitizePhone(form.phone)
  clearFieldError('phone')
}

const handleEmailInput = () => {
  form.email = sanitizeEmail(form.email)
  clearFieldError('email')
}

const trimForm = () => {
  form.name = form.name.trim()
  form.phone = form.phone.trim()
  form.email = form.email.trim()
  form.experience = form.experience.trim()
}

const validateRequiredAndMaxLength = (field, maxLength, requiredKey, maxKey) => {
  const value = form[field].trim()
  if (!value) {
    fieldErrors[field] = t(requiredKey)
    return false
  }
  if (value.length > maxLength) {
    fieldErrors[field] = t(maxKey, { max: maxLength })
    return false
  }
  return true
}

const validateForm = () => {
  trimForm()
  Object.keys(fieldErrors).forEach((field) => {
    fieldErrors[field] = ''
  })

  let valid = true
  valid = validateRequiredAndMaxLength('name', fieldLimits.name, 'careerModal.nameRequired', 'careerModal.nameMaxLength') && valid
  valid = validateRequiredAndMaxLength('experience', fieldLimits.experience, 'careerModal.experienceRequired', 'careerModal.experienceMaxLength') && valid

  if (!form.phone) {
    fieldErrors.phone = t('careerModal.phoneRequired')
    valid = false
  } else if (!isPhoneCharactersValid(form.phone)) {
    fieldErrors.phone = t('careerModal.phoneInvalidChars')
    valid = false
  } else if (form.phone.length < fieldLimits.phoneMin || form.phone.length > fieldLimits.phoneMax) {
    fieldErrors.phone = t('careerModal.phoneLength', {
      min: fieldLimits.phoneMin,
      max: fieldLimits.phoneMax
    })
    valid = false
  }

  if (!form.email) {
    fieldErrors.email = t('careerModal.emailRequired')
    valid = false
  } else if (!isEmailCharactersValid(form.email)) {
    fieldErrors.email = t('careerModal.emailInvalidChars')
    valid = false
  } else if (form.email.length > fieldLimits.email) {
    fieldErrors.email = t('careerModal.emailMaxLength', { max: fieldLimits.email })
    valid = false
  } else if (!emailPattern.test(form.email)) {
    fieldErrors.email = t('careerModal.emailFormat')
    valid = false
  }

  return valid
}

const selectResume = () => {
  resumeInputRef.value && resumeInputRef.value.click()
}

const handleResumeChange = (event) => {
  const file = event.target.files && event.target.files[0]
  fileError.value = ''
  if (!file) {
    clearResume()
    return
  }
  const extension = file.name.split('.').pop().toLowerCase()
  if (!allowedResumeExtensions.includes(extension)) {
    fileError.value = t('careerModal.attachmentTypeError')
    event.target.value = ''
    clearResume()
    return
  }
  if (file.size > maxResumeSize) {
    fileError.value = t('careerModal.attachmentSizeError')
    event.target.value = ''
    clearResume()
    return
  }
  form.resumeFile = file || null
  form.resumeFileName = file ? file.name : ''
}

const clearResume = () => {
  form.resumeFile = null
  form.resumeFileName = ''
  if (resumeInputRef.value) {
    resumeInputRef.value.value = ''
  }
}

const handleSubmit = async () => {
  if (isSubmitting.value) {
    return
  }
  if (!validateForm() || fileError.value) {
    return
  }

  try {
    isSubmitting.value = true
    await submitCareerContact({
      type: props.contactType,
      name: form.name,
      mobile: form.phone,
      email: form.email,
      industryExperience: form.experience,
      attachmentFile: form.resumeFile
    })
    showToast(t('careerModal.successMessage'))
    closeModal()
  } catch (error) {
    showToast(error.message || t('careerModal.errorMessage'), 'error')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 39, 64, 0.75);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-container {
  background: rgba(255, 255, 255, 0.76);
  border-radius: 24px;
  width: 100%;
  max-width: 760px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow:
    0 25px 80px rgba(0, 0, 0, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.2) inset;
  animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  background: linear-gradient(135deg, rgba(15, 39, 64, 0.95) 0%, rgba(26, 58, 92, 0.92) 100%);
  padding: 24px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 24px 24px 0 0;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 20% 50%, rgba(52, 152, 255, 0.2) 0%, transparent 50%),
    radial-gradient(circle at 80% 50%, rgba(255, 255, 255, 0.05) 0%, transparent 40%);
  pointer-events: none;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  justify-content: center;
  position: relative;
  z-index: 1;
}

.briefcase-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--secondary-color);
  background: rgba(255, 255, 255, 0.12);
  box-shadow: 0 4px 15px rgba(52, 152, 255, 0.2);
}

.briefcase-icon svg {
  width: 24px;
  height: 24px;
}

.modal-title {
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.close-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  cursor: pointer;
  padding: 10px;
  border-radius: 10px;
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-50%) scale(1.05);
}

.close-btn svg {
  width: 20px;
  height: 20px;
}

.modal-body {
  padding: 36px;
}

.form-row {
  display: grid;
  gap: 20px;
  margin-bottom: 20px;
}

.form-row.three-cols {
  grid-template-columns: repeat(3, 1fr);
}

.form-group {
  display: flex;
  flex-direction: column;
}

.attachment-group {
  margin-top: 20px;
}

.form-group label {
  color: var(--primary-color);
  font-size: 0.9rem;
  margin-bottom: 10px;
  font-weight: 600;
}

.required {
  color: #e74c3c;
}

.form-group input,
.form-group textarea {
  padding: 14px 18px;
  border: 1.5px solid rgba(255, 255, 255, 0.7);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.5);
  color: var(--text-dark);
  font-size: 0.98rem;
  transition: all 0.3s ease;
  font-family: inherit;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: rgba(102, 102, 102, 0.6);
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--secondary-color);
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 0 4px rgba(52, 152, 255, 0.15), 0 4px 12px rgba(52, 152, 255, 0.1);
}

.form-group textarea {
  resize: vertical;
  min-height: 120px;
}

.sr-only-file {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.attachment-line {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 32px;
  flex-wrap: wrap;
}

.attachment-link,
.attachment-clear {
  border: none;
  background: transparent;
  color: var(--secondary-color);
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}

.attachment-link:hover,
.attachment-clear:hover {
  color: #2980b9;
  text-decoration: underline;
}

.attachment-clear {
  color: #9aa7b4;
  font-weight: 600;
}

.attachment-name {
  color: var(--text-light);
  font-size: 0.86rem;
  line-height: 1.5;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.field-meta {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
}

.form-error {
  margin-top: 8px;
  color: #e74c3c !important;
  font-size: 0.84rem;
  line-height: 1.5;
}

.field-meta .form-error {
  margin-top: 0;
}

.char-count {
  margin-left: auto;
  color: var(--text-light);
  font-size: 0.82rem;
  line-height: 1.5;
  white-space: nowrap;
}

.submit-section {
  text-align: center;
  margin-top: 32px;
}

.submit-btn {
  background: linear-gradient(135deg, var(--secondary-color) 0%, #2980b9 100%);
  color: #fff;
  border: none;
  padding: 16px 64px;
  border-radius: 14px;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(52, 152, 255, 0.3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.submit-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #2980b9 0%, var(--secondary-color) 100%);
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(52, 152, 255, 0.4);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: submitSpin 0.8s linear infinite;
}

@keyframes submitSpin {
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 16px;
  }

  .modal-container {
    max-height: 95vh;
    border-radius: 24px;
  }

  .modal-header {
    padding: 18px 24px;
  }

  .modal-title {
    font-size: 1.25rem;
  }

  .briefcase-icon {
    width: 40px;
    height: 40px;
  }

  .briefcase-icon svg {
    width: 20px;
    height: 20px;
  }

  .close-btn {
    right: 16px;
    padding: 8px;
  }

  .modal-body {
    padding: 24px;
  }

  .form-row.three-cols {
    grid-template-columns: 1fr;
  }

  .form-group input,
  .form-group textarea {
    padding: 12px 16px;
    font-size: 0.95rem;
  }

  .submit-btn {
    padding: 14px 48px;
    font-size: 1rem;
    width: 100%;
  }
}
</style>
